import { fromMinorUnits } from '@/api/map/agent'
import { fromMinorUnitsSigned } from '@/api/map/pnl'
import { previousRange, toAgentTurnoverRow, toDailyPoint, type AnalyticsDailyRow } from '@/api/map/turnover'
import { toTransferRowView, type AnalyticsTransferRow } from '@/api/map/transfers'
import type { OverviewSnapshot } from '@/data/api'
import type { Account, DailyPoint, Transfer } from '@/data/types'

export { previousRange }

/** OpenAPI `OverviewSnapshot` (analytics-service). */
export interface AnalyticsOverviewSnapshot {
  asOf?: string
  martBuiltAt?: string | null
  martStale?: boolean
  agentId: string
  turnoverMinor: string
  bookNetMinor: string
  activeMembers: number
  subtreeMembers?: number
  exposure: {
    valueMinor?: string
    stale: boolean
    projectedAt?: string | null
  }
  trend: AnalyticsDailyRow[]
  attention: {
    dormant30d?: number
    neverPlacedABet?: number
    creditLimitNearlyUsed?: number
  }
}

export type OverviewAttentionCounts = {
  dormant30d: number
  neverPlacedABet: number
  creditLimitNearlyUsed: number
}

/**
 * Console view of Overview — contract-aligned.
 * Live never invents sport / open-bet / unmatched figures (OpenAPI non-goals).
 */
export type OverviewView = {
  asOf?: string
  martStale?: boolean
  live: boolean
  account: Account | null
  turnover: number
  bookNet: number
  priorTurnover: number
  priorBookNet: number
  activeMembers: number
  subtreeMembers: number | null
  exposure: number | null
  exposureStale: boolean
  daily: DailyPoint[]
  previous: DailyPoint[]
  attentionCounts: OverviewAttentionCounts
  /** Fixture-only per-account reasons. Live uses counts (+ downline filters). */
  attentionItems: OverviewSnapshot['attention']
  topAgents: OverviewSnapshot['topAgents']
  recentTransfers: Transfer[]
  bySport: OverviewSnapshot['bySport']
  openBets: number | null
  unmatchedStake: number | null
  sportUnavailable: boolean
  openPositionUnavailable: boolean
  agentsUnavailable: boolean
  transfersUnavailable: boolean
  /** True when GET /overview failed (502 etc.) — page stays up; sections show No data found. */
  overviewUnavailable: boolean
  /** Optional operator-facing reason (never secrets). */
  loadError?: string
}

function emptyCounts(): OverviewAttentionCounts {
  return { dormant30d: 0, neverPlacedABet: 0, creditLimitNearlyUsed: 0 }
}

function sumDaily(points: DailyPoint[]): { turnover: number; bookNet: number } {
  return points.reduce(
    (acc, d) => ({
      turnover: acc.turnover + d.turnover,
      bookNet: acc.bookNet + d.netPnl,
    }),
    { turnover: 0, bookNet: 0 },
  )
}

/** Map OpenAPI overview (+ optional companion pages) → console view. */
export function toOverviewView(input: {
  current: AnalyticsOverviewSnapshot
  previous?: AnalyticsOverviewSnapshot | null
  account: Account | null
  agentTurnoverRows?: { key: string; label?: string; turnoverMinor: string; bookNetMinor?: string; members?: number }[] | null
  transferRows?: AnalyticsTransferRow[] | null
  selfId: string
}): OverviewView {
  const { current, previous, account, agentTurnoverRows, transferRows, selfId } = input
  const daily = (current.trend ?? []).map(toDailyPoint)
  const previousDaily = (previous?.trend ?? []).map(toDailyPoint)
  const priorFromSnap = previous
    ? {
        turnover: fromMinorUnits(previous.turnoverMinor),
        bookNet: fromMinorUnitsSigned(previous.bookNetMinor),
      }
    : sumDaily(previousDaily)

  const exposureStale = Boolean(current.exposure?.stale)
  const exposure =
    exposureStale || current.exposure?.valueMinor === undefined
      ? null
      : fromMinorUnits(current.exposure.valueMinor)

  const topAgents =
    agentTurnoverRows === null
      ? []
      : (agentTurnoverRows ?? [])
          .map((row) => toAgentTurnoverRow(row, selfId))
          .filter((row): row is NonNullable<typeof row> => row !== null)
          .sort((a, b) => b.turnover - a.turnover)
          .slice(0, 7)

  const recentTransfers =
    transferRows === null
      ? []
      : (transferRows ?? []).slice(0, 8).map((row) => {
          const { from: _f, to: _t, ...transfer } = toTransferRowView(row)
          return transfer
        })

  return {
    asOf: current.asOf,
    martStale: Boolean(current.martStale),
    live: true,
    account,
    turnover: fromMinorUnits(current.turnoverMinor),
    bookNet: fromMinorUnitsSigned(current.bookNetMinor),
    priorTurnover: priorFromSnap.turnover,
    priorBookNet: priorFromSnap.bookNet,
    activeMembers: current.activeMembers ?? 0,
    subtreeMembers:
      typeof current.subtreeMembers === 'number' ? current.subtreeMembers : null,
    exposure,
    exposureStale,
    daily,
    previous: previousDaily,
    attentionCounts: {
      dormant30d: current.attention?.dormant30d ?? 0,
      neverPlacedABet: current.attention?.neverPlacedABet ?? 0,
      creditLimitNearlyUsed: current.attention?.creditLimitNearlyUsed ?? 0,
    },
    attentionItems: [],
    topAgents,
    recentTransfers,
    bySport: [],
    openBets: null,
    unmatchedStake: null,
    sportUnavailable: true,
    openPositionUnavailable: true,
    agentsUnavailable: agentTurnoverRows === null,
    transfersUnavailable: transferRows === null,
    overviewUnavailable: false,
  }
}

/**
 * Soft-fail shell when GET /overview is unreachable (502 / network).
 * Keeps the page structure; every analytics section reads as No data found.
 * Companion payloads (turnover / transfers) still attach when they succeeded.
 */
export function emptyLiveOverview(input: {
  account: Account | null
  agentTurnoverRows?: { key: string; label?: string; turnoverMinor: string; bookNetMinor?: string; members?: number }[] | null
  transferRows?: AnalyticsTransferRow[] | null
  selfId: string
  loadError?: string
}): OverviewView {
  const base = toOverviewView({
    current: {
      agentId: input.selfId,
      turnoverMinor: '0',
      bookNetMinor: '0',
      activeMembers: 0,
      exposure: { stale: true },
      trend: [],
      attention: {},
    },
    previous: null,
    account: input.account,
    agentTurnoverRows: input.agentTurnoverRows ?? null,
    transferRows: input.transferRows ?? null,
    selfId: input.selfId,
  })
  return {
    ...base,
    exposure: null,
    exposureStale: false,
    subtreeMembers: null,
    overviewUnavailable: true,
    loadError: input.loadError,
  }
}

/** Fixture `getOverview` → same view shape (demo keeps sport / open bets / attention rows). */
export function fromFixtureOverview(snapshot: OverviewSnapshot, asOf?: string): OverviewView {
  const prior = sumDaily(snapshot.previous)
  const counts = emptyCounts()
  for (const item of snapshot.attention) {
    if (item.reason.toLowerCase().includes('dormant')) counts.dormant30d += 1
    else if (item.reason.toLowerCase().includes('never')) counts.neverPlacedABet += 1
    else if (item.severity === 'error' || item.reason.toLowerCase().includes('credit')) {
      counts.creditLimitNearlyUsed += 1
    } else counts.creditLimitNearlyUsed += 1
  }

  return {
    asOf: asOf ?? new Date().toISOString(),
    martStale: false,
    live: false,
    account: snapshot.account,
    turnover: sumDaily(snapshot.daily).turnover,
    bookNet: sumDaily(snapshot.daily).bookNet,
    priorTurnover: prior.turnover,
    priorBookNet: prior.bookNet,
    activeMembers: snapshot.activeMembers,
    subtreeMembers: snapshot.totalMembers,
    exposure: snapshot.account.exposure,
    exposureStale: false,
    daily: snapshot.daily,
    previous: snapshot.previous,
    attentionCounts: counts,
    attentionItems: snapshot.attention,
    topAgents: snapshot.topAgents,
    recentTransfers: snapshot.recentTransfers,
    bySport: snapshot.bySport,
    openBets: snapshot.openBets,
    unmatchedStake: snapshot.unmatchedStake,
    sportUnavailable: false,
    openPositionUnavailable: false,
    agentsUnavailable: false,
    transfersUnavailable: false,
    overviewUnavailable: false,
  }
}

export function attentionTotal(counts: OverviewAttentionCounts): number {
  return counts.dormant30d + counts.neverPlacedABet + counts.creditLimitNearlyUsed
}
